/**
 * OpenAI Ads Measurement Pixel (oaiq) — page d'accueil Studio et funnel signup.
 * Désactiver : VUE_APP_OPENAI_ADS_PIXEL_ENABLED=false
 * Pixel ID : VUE_APP_OPENAI_ADS_PIXEL_ID
 * Debug console : VUE_APP_OPENAI_ADS_PIXEL_DEBUG=true
 */
import Cookies from 'js-cookie';
import { isLightBootPublicPath } from '../utils/marketingPages.js';

const ENABLED =
  String(process.env.VUE_APP_OPENAI_ADS_PIXEL_ENABLED ?? 'true').toLowerCase() !== 'false';

const PIXEL_ID = String(process.env.VUE_APP_OPENAI_ADS_PIXEL_ID || '').trim();

const DEBUG =
  String(process.env.VUE_APP_OPENAI_ADS_PIXEL_DEBUG ?? 'false').toLowerCase() === 'true';

const SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';

let initDone = false;
let consentGranted = false;
let sdkBootstrapPromise = null;
let queuedPageViewPath = null;

/** Noms internes Studio → événements standard OpenAI Ads. */
const STANDARD_EVENT_MAP = {
  PageView: 'page_viewed',
  CompleteRegistration: 'registration_completed',
};

/** Étape principale « lead » — seul événement standard lead_created du funnel. */
const OPENAI_LEAD_EVENT_NAMES = new Set([
  'Free Test Completed',
  'Lead',
]);

function mixpanelNameToOpenAiCustom(name) {
  const normalized = String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
  return normalized || 'studio_event';
}

/** Alias legacy → noms Mixpanel / custom OpenAI. */
const LEGACY_CUSTOM_EVENT_ALIASES = {
  MagicLinkRequested: 'free_test_magic_link_requested',
  Lead: null,
};

function resolveOpenAiMeasureCall(internalName) {
  const name = String(internalName || '').trim();
  if (!name) return null;

  const standard = STANDARD_EVENT_MAP[name];
  if (standard) {
    return { measureName: standard, customName: null };
  }

  if (OPENAI_LEAD_EVENT_NAMES.has(name)) {
    return { measureName: 'lead_created', customName: null };
  }

  const legacyCustom = LEGACY_CUSTOM_EVENT_ALIASES[name];
  if (legacyCustom) {
    return { measureName: 'custom', customName: legacyCustom };
  }

  return {
    measureName: 'custom',
    customName: mixpanelNameToOpenAiCustom(name),
  };
}

function deferOpenAiAdsWork(task) {
  if (typeof window === 'undefined') return;
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(task, { timeout: 2500 });
    return;
  }
  window.setTimeout(task, 150);
}

function getConsentCookieRaw() {
  if (typeof document === 'undefined') return null;
  let value = Cookies.get('consent');
  if (value !== undefined && value !== null && value !== '') return String(value);
  const match = document.cookie.match(/(?:^|;\s*)consent=([^;]*)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1].trim());
  } catch (_err) {
    return match[1];
  }
}

function isConsentGranted() {
  const consent = getConsentCookieRaw();
  if (consent == null || consent === '') return false;
  const normalized = String(consent).trim().toLowerCase();
  return (
    normalized === '1' ||
    normalized === 'true' ||
    normalized === 'yes' ||
    normalized === 'on'
  );
}

function normalizePath(path = '') {
  const source =
    path ||
    (typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : '/');
  try {
    return new URL(String(source), 'https://studio.mydid.local').pathname;
  } catch (_err) {
    return String(source || '/').split('?')[0] || '/';
  }
}

function injectOaiqScript() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  if (window.oaiq) return true;

  (function bootstrapOaiq(w, d, s, u) {
    if (w.oaiq) return;
    const q = function oaiqQueue() {
      q.q.push(arguments);
    };
    q.q = [];
    w.oaiq = q;
    const js = d.createElement(s);
    js.async = true;
    js.src = u;
    const anchor = d.getElementsByTagName(s)[0];
    anchor.parentNode.insertBefore(js, anchor);
  })(window, document, 'script', SDK_URL);

  return true;
}

function bootstrapOaiq() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(false);
  }
  if (window.oaiq) return Promise.resolve(true);
  if (sdkBootstrapPromise) return sdkBootstrapPromise;

  sdkBootstrapPromise = new Promise((resolve) => {
    deferOpenAiAdsWork(() => {
      resolve(injectOaiqScript());
    });
  });
  return sdkBootstrapPromise;
}

function callOaiq(...args) {
  if (typeof window === 'undefined' || typeof window.oaiq !== 'function') return false;
  window.oaiq(...args);
  return true;
}

function syncConsentState() {
  consentGranted = isConsentGranted();
  callOaiq('consent', consentGranted);
  return consentGranted;
}

function buildEventData(openAiEventName, params = {}) {
  switch (openAiEventName) {
    case 'page_viewed':
      return {
        type: 'contents',
        contents: [
          {
            id: String(params.content_id || params.path || 'landing').slice(0, 128),
            name: String(params.content_name || params.page_title || 'Landing page').slice(0, 256),
            content_type: 'page',
          },
        ],
      };
    case 'lead_created':
    case 'registration_completed':
      return { type: 'customer_action' };
    default:
      return { type: 'custom' };
  }
}

function buildEventOptions(openAiEventName, params = {}) {
  const options = {};
  const eventId = String(
    params.event_id || params.$insert_id || params.insert_id || ''
  ).trim();
  if (eventId) options.event_id = eventId;

  if (openAiEventName === 'custom') {
    const customName = String(
      params.openai_custom_event_name || params.custom_event_name || params.content_name || ''
    ).trim();
    if (customName) options.custom_event_name = customName;
  }

  return Object.keys(options).length ? options : null;
}

async function ensureOpenAiAdsPixelInitialized(path = null, options = {}) {
  if (!ENABLED || !PIXEL_ID) return false;
  if (!options.allowPublicLightBootPath && isLightBootPublicPath(normalizePath(path))) {
    return false;
  }

  await bootstrapOaiq();
  if (typeof window === 'undefined' || typeof window.oaiq !== 'function') return false;

  syncConsentState();

  if (initDone) return true;

  const initPayload = { pixelId: PIXEL_ID };
  if (DEBUG) initPayload.debug = true;
  callOaiq('init', initPayload);
  initDone = true;
  return true;
}

export function initOpenAiAdsPixel() {
  return ensureOpenAiAdsPixelInitialized(null, { allowPublicLightBootPath: true });
}

export function trackOpenAiAdsPixelPageView(path = null) {
  if (!ENABLED || !PIXEL_ID) return false;

  const pagePath = path || (
    typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : '/'
  );
  const pageTitle =
    typeof document !== 'undefined' ? document.title : 'Landing page';

  void ensureOpenAiAdsPixelInitialized(path, { allowPublicLightBootPath: true }).then((ready) => {
    if (!ready || !isConsentGranted()) return;
    trackOpenAiAdsPixel('PageView', {
      path: pagePath,
      content_id: normalizePath(pagePath),
      content_name: pageTitle,
      page_title: pageTitle,
    });
  });
  return true;
}

export function trackOpenAiAdsPixel(eventName, params = {}) {
  if (!ENABLED || !PIXEL_ID || !eventName) return false;

  const internalName = String(eventName).trim();
  if (!internalName) return false;

  const payload = params && typeof params === 'object' ? params : {};
  const resolved = resolveOpenAiMeasureCall(internalName);
  if (!resolved) return false;

  const { measureName: openAiEventName, customName } = resolved;
  const measurePayload = customName
    ? { ...payload, openai_custom_event_name: customName }
    : payload;
  const eventData = buildEventData(openAiEventName, measurePayload);
  const eventOptions = buildEventOptions(openAiEventName, measurePayload);

  void ensureOpenAiAdsPixelInitialized(null, { allowPublicLightBootPath: true }).then((ready) => {
    if (!ready || !isConsentGranted()) return;
    if (eventOptions) {
      callOaiq('measure', openAiEventName, eventData, eventOptions);
      return;
    }
    callOaiq('measure', openAiEventName, eventData);
  });
  return true;
}

export function applyOpenAiAdsPixelConsent(accepted) {
  consentGranted = !!accepted;
  if (!ENABLED || !PIXEL_ID) return false;

  void ensureOpenAiAdsPixelInitialized(null, { allowPublicLightBootPath: true }).then((ready) => {
    if (!ready) return;
    callOaiq('consent', consentGranted);
    if (!consentGranted) {
      queuedPageViewPath = null;
      return;
    }
    const currentPath =
      queuedPageViewPath ||
      (typeof window !== 'undefined'
        ? window.location.pathname + window.location.search
        : null);
    queuedPageViewPath = null;
    if (currentPath) trackOpenAiAdsPixelPageView(currentPath);
  });
  return consentGranted;
}
